<template>
    <div class="inventory-history">
        <div class="page-header">
            <div class="header-left">
                <PageTitle :title="'Historique des inventaires'" :description="(pagination.total || 0) + ' inventaire(s) au total'" />
            </div>
            <BaseButton @click="startNewInventory" class="btn-primary">
                <i class="fas fa-plus" style="width: 18px; height: 18px;"></i>
                Lancer un inventaire
            </BaseButton>
        </div>

        <div class="filters-section">
            <div class="search-bar">
                <i class="fas fa-search" style="width: 20px; height: 20px;"></i>
                <input v-model="searchQuery" type="text" placeholder="Rechercher par référence..." @input="handleSearch"
                    class="search-input" />
            </div>

            <div class="filters-bar">
                <div class="filter-group">
                    <label>Statut</label>
                    <select v-model="filters.status" @change="loadInventories" class="filter-select">
                        <option value="">Tous</option>
                        <option value="draft">Brouillon</option>
                        <option value="in_progress">En cours</option>
                        <option value="completed">Terminé</option>
                        <option value="approved">Approuvé</option>
                        <option value="rejected">Rejeté</option>
                    </select>
                </div>
            </div>
        </div>

        <div class="table-container">
            <div v-if="loading" class="loading-state">
                <div class="spinner"></div>
                <p>Chargement des inventaires...</p>
            </div>

            <div v-else-if="inventories.length === 0" class="empty-state">
                <i class="fas fa-boxes" style="width: 64px; height: 64px; font-size: 64px;"></i>
                <p>Aucun inventaire trouvé</p>
                <BaseButton @click="startNewInventory" class="btn-secondary">Créer le premier inventaire</BaseButton>
            </div>

            <table v-else class="inventory-table">
                <thead>
                    <tr>
                        <th>Référence</th>
                        <th>Statut</th>
                        <th>Créé par</th>
                        <th>Date création</th>
                        <th>Produits</th>
                        <th>Progression</th>
                        <th>Date validation</th>
                        <th class="actions-column">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="inventory in inventories" :key="inventory.id" class="table-row">
                        <td class="reference-cell">
                            <strong>{{ inventory.reference_number }}</strong>
                        </td>
                        <td>
                            <BaseBadge :class="['status-badge', `status-${inventory.status}`]">
                                {{ getStatusLabel(inventory.status) }}
                            </BaseBadge>
                        </td>
                        <td>{{ inventory.user?.name || '-' }}</td>
                        <td>{{ formatDate(inventory.created_at) }}</td>
                        <td>
                            <div class="items-info">
                                <span class="items-total">{{ inventory.total_items }}</span>
                                <span class="items-detail">({{ inventory.counted_items }} comptés)</span>
                            </div>
                        </td>
                        <td>
                            <div class="progress-cell">
                                <div class="progress-bar-small">
                                    <div class="progress-fill-small"
                                        :style="{ width: inventory.progress_percentage + '%' }"></div>
                                </div>
                                <span class="progress-text">{{ inventory.progress_percentage }}%</span>
                            </div>
                        </td>
                        <td>{{ inventory.approved_at ? formatDate(inventory.approved_at) : '-' }}</td>
                        <td class="actions-cell">
                            <BaseButton variant="icon" size="sm" @click="viewInventory(inventory)" title="Voir details"
                                class="text-slate-600 hover:bg-slate-100">
                                <BaseIcon name="eye" :size="16" />
                            </BaseButton>

                            <BaseButton v-if="inventory.status === 'draft' || inventory.status === 'in_progress'"
                                variant="icon" size="sm" @click="continueInventory(inventory)" title="Continuer"
                                class="text-emerald-600 hover:bg-emerald-50">
                                <i class="fas fa-play" style="width: 16px; height: 16px;"></i>
                            </BaseButton>

                            <BaseButton v-if="inventory.status === 'completed'" variant="icon" size="sm"
                                @click="approveInventory(inventory)" title="Approuver"
                                class="text-green-600 hover:bg-green-50">
                                <BaseIcon name="check" :size="16" />
                            </BaseButton>

                            <BaseButton v-if="inventory.status === 'completed'" variant="icon" size="sm"
                                @click="rejectInventory(inventory)" title="Rejeter" class="text-red-600 hover:bg-red-50">
                                <BaseIcon name="x-circle" :size="16" />
                            </BaseButton>

                            <BaseButton v-if="inventory.status === 'draft'" variant="icon" size="sm"
                                @click="deleteInventory(inventory)" title="Supprimer" class="text-red-600 hover:bg-red-50">
                                <BaseIcon name="trash" :size="16" />
                            </BaseButton>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="pagination.last_page > 1" class="pagination">
            <BaseButton @click="changePage(pagination.current_page - 1)" :disabled="pagination.current_page === 1"
                class="pagination-btn">
                Précédent
            </BaseButton>
            <span class="pagination-info">
                Page {{ pagination.current_page }} sur {{ pagination.last_page }}
            </span>
            <BaseButton @click="changePage(pagination.current_page + 1)"
                :disabled="pagination.current_page === pagination.last_page" class="pagination-btn">
                Suivant
            </BaseButton>
        </div>
    </div>
</template>

<script>
import physicalInventoryService from '@/services/physicalInventoryService';
import PageTitle from '@/components/common/PageTitle.vue';
import BaseBadge from '@/components/base/BaseBadge.vue';
import { useToastStore } from '@/store/modules/toastStore';

export default {
    name: 'InventoryHistory',
    components: {
        PageTitle,
        BaseBadge
    },
    setup() {
        const toastStore = useToastStore();
        return { toastStore };
    },
    data() {
        return {
            loading: false,
            searchQuery: '',
            searchTimeout: null,
            filters: {
                status: ''
            },
            inventories: [],
            pagination: {
                current_page: 1,
                last_page: 1,
                per_page: 20,
                total: 0
            }
        }
    },
    mounted() {
        this.loadInventories();
    },
    methods: {
        async loadInventories() {
            this.loading = true;
            try {
                const params = {
                    page: this.pagination.current_page
                };

                if (this.filters.status) {
                    params.status = this.filters.status;
                }

                if (this.searchQuery) {
                    params.search = this.searchQuery;
                }

                const response = await physicalInventoryService.getInventories(params);

                this.inventories = response.data || [];
                this.pagination = {
                    current_page: response.current_page,
                    last_page: response.last_page,
                    per_page: response.per_page,
                    total: response.total
                };
            } catch (error) {
                console.error('Erreur lors du chargement des inventaires:', error);
                this.toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors du chargement des inventaires'
                });
            } finally {
                this.loading = false;
            }
        },

        handleSearch() {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                this.pagination.current_page = 1;
                this.loadInventories();
            }, 500);
        },

        changePage(page) {
            if (page >= 1 && page <= this.pagination.last_page) {
                this.pagination.current_page = page;
                this.loadInventories();
            }
        },

        startNewInventory() {
            this.$router.push({ name: 'inventory-create' });
        },

        viewInventory(inventory) {
            this.$router.push({ name: 'inventory-details', params: { id: inventory.id } });
        },

        continueInventory() {
            this.$router.push({ name: 'doing-inventory' });
        },

        async approveInventory(inventory) {
            if (!confirm('Êtes-vous sûr de vouloir approuver cet inventaire ? Les ajustements de stock seront appliqués.')) {
                return;
            }

            try {
                await physicalInventoryService.approveInventory(inventory.id);
                this.toastStore.addToast({
                    type: 'success',
                    message: 'Inventaire approuvé avec succès'
                });
                this.loadInventories();
            } catch (error) {
                console.error('Erreur lors de l\'approbation:', error);
                this.toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors de l\'approbation de l\'inventaire'
                });
            }
        },

        async rejectInventory(inventory) {
            const notes = prompt('Raison du rejet (optionnel):');
            if (notes === null) return;

            try {
                await physicalInventoryService.rejectInventory(inventory.id, notes);
                this.toastStore.addToast({
                    type: 'success',
                    message: 'Inventaire rejeté'
                });
                this.loadInventories();
            } catch (error) {
                console.error('Erreur lors du rejet:', error);
                this.toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors du rejet de l\'inventaire'
                });
            }
        },

        async deleteInventory(inventory) {
            if (!confirm('Êtes-vous sûr de vouloir supprimer cet inventaire ?')) {
                return;
            }

            try {
                await physicalInventoryService.deleteInventory(inventory.id);
                this.toastStore.addToast({
                    type: 'success',
                    message: 'Inventaire supprimé'
                });
                this.loadInventories();
            } catch (error) {
                console.error('Erreur lors de la suppression:', error);
                this.toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors de la suppression de l\'inventaire'
                });
            }
        },

        getStatusLabel(status) {
            return physicalInventoryService.getStatusLabel(status);
        },
        getStatusVariant(status) {
            const variants = {
                draft: 'gray',
                in_progress: 'info',
                completed: 'warning',
                approved: 'success',
                rejected: 'danger'
            };
            return variants[status] || 'gray';
        },

        formatDate(dateString) {
            if (!dateString) return '-';
            const date = new Date(dateString);
            return date.toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
    }
}
</script>

<style scoped>
.page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;
}

.header-left {
    flex: 1;
}

.page-title {
    font-size: 28px;
    font-weight: 600;
    color: #1a202c;
    margin: 0 0 8px 0;
}

.page-subtitle {
    color: #718096;
    font-size: 14px;
}

.btn-primary {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: #3FDA4D;
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-primary:hover {
    background: #35c441;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(63, 218, 77, 0.3);
}

.filters-section {
    background: white;
    padding: 20px;
    border-radius: 12px;
    margin-bottom: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.search-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    padding: 12px;
    background: #f7fafc;
    border-radius: 8px;
}

.search-bar svg {
    color: #718096;
    flex-shrink: 0;
}

.search-input {
    flex: 1;
    border: none;
    background: transparent;
    font-size: 14px;
    outline: none;
    color: #1a202c;
}

.search-input::placeholder {
    color: #a0aec0;
}

.filters-bar {
    display: flex;
    gap: 16px;
}

.filter-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.filter-group label {
    font-size: 13px;
    font-weight: 500;
    color: #4a5568;
}

.filter-select {
    padding: 8px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 14px;
    min-width: 200px;
    cursor: pointer;
    background: white;
}

.filter-select:focus {
    outline: none;
    border-color: #3FDA4D;
}

.table-container {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.loading-state {
    padding: 80px 40px;
    text-align: center;
}

.spinner {
    width: 40px;
    height: 40px;
    margin: 0 auto 20px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3FDA4D;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.loading-state p {
    color: #718096;
    font-size: 14px;
}

.empty-state {
    padding: 80px 40px;
    text-align: center;
}

.empty-state svg {
    color: #cbd5e0;
    margin-bottom: 20px;
}

.empty-state p {
    color: #718096;
    font-size: 16px;
    margin-bottom: 20px;
}

.btn-secondary {
    padding: 10px 20px;
    background: white;
    color: #3FDA4D;
    border: 2px solid #3FDA4D;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-secondary:hover {
    background: #3FDA4D;
    color: white;
}

.inventory-table {
    width: 100%;
    border-collapse: collapse;
}

.inventory-table thead {
    background: #f7fafc;
    border-bottom: 2px solid #e2e8f0;
}

.inventory-table th {
    padding: 16px;
    text-align: left;
    font-size: 13px;
    font-weight: 600;
    color: #4a5568;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.inventory-table th.actions-column {
    text-align: center;
}

.inventory-table tbody tr {
    border-bottom: 1px solid #f7fafc;
    transition: background-color 0.2s;
}

.inventory-table tbody tr:hover {
    background: #f7fafc;
}

.inventory-table td {
    padding: 16px;
    font-size: 14px;
    color: #2d3748;
}

.reference-cell strong {
    color: #1a202c;
    font-weight: 600;
}

.items-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.items-total {
    font-weight: 600;
    color: #1a202c;
}

.items-detail {
    font-size: 12px;
    color: #718096;
}

.progress-cell {
    display: flex;
    align-items: center;
    gap: 12px;
}

.progress-bar-small {
    flex: 1;
    height: 6px;
    background: #e2e8f0;
    border-radius: 3px;
    overflow: hidden;
    max-width: 120px;
}

.progress-fill-small {
    height: 100%;
    background: linear-gradient(90deg, #3FDA4D 0%, #35c441 100%);
    transition: width 0.3s ease;
}

.progress-text {
    font-size: 13px;
    font-weight: 600;
    color: #3FDA4D;
    min-width: 40px;
}

.actions-cell {
    text-align: center;
}


.pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    margin-top: 24px;
}

.pagination-btn {
    padding: 8px 16px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    color: #4a5568;
    transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
    background: #f7fafc;
    border-color: #3FDA4D;
    color: #3FDA4D;
}

.pagination-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.pagination-info {
    font-size: 14px;
    color: #718096;
}
</style>
